import { Button, Icon, type IconType } from "@pepperstone/pepper-ui-kit";
import clsx from "clsx";
import {
  type CloseButtonProps,
  type IconProps,
  type ToastClassName,
  ToastContainer,
  type TypeOptions,
} from "react-toastify";
import styles from "./Toast.module.css";

const CloseButton = ({ closeToast }: CloseButtonProps) => (
  <Button
    variant="ps-btn-transparent"
    type="button"
    className={styles.CloseButton}
    size="ps-btn-xs"
    icon="cross"
    onClick={closeToast}
    aria-label="Close notification"
  />
);

// Per-type icon, mirroring the status-driven styling used by the bank-statement
// file card. Each toast type renders its own icon and gets a matching CSS-module
// class (see `getToastClassName`), so the look of error / success / info /
// warning can be tuned independently in Toast.module.css. The icon deliberately
// has no `color` prop: pepper-ui-kit's colour classes are `!important` and would
// override our tokens, so we let the icon inherit the toast's text colour
// (`--toastify-text-color-{type}`, set per type in Toast.module.css) instead.
const TYPE_ICON: Partial<Record<TypeOptions, { icon: IconType }>> = {
  success: { icon: "check-mark-circle" },
  error: { icon: "alert-triangle" },
  warning: { icon: "alert-circle" },
  info: { icon: "alert-circle" },
};

const TYPE_CLASS: Partial<Record<TypeOptions, string>> = {
  success: styles.success,
  error: styles.error,
  warning: styles.warning,
  info: styles.info,
};

const ToastIcon = ({ type }: IconProps) => {
  const config = TYPE_ICON[type];
  return config ? <Icon icon={config.icon} /> : null;
};

// react-toastify supplies `defaultClassName` (the built-in
// `Toastify__toast` + theme + per-type classes) when `toastClassName` is a
// function and expects us to include it. The colored-theme rule that reads our
// `--toastify-*-error` variables lives on `.Toastify__toast--error`, so dropping
// `defaultClassName` would leave those custom properties set but never consumed.
const getToastClassName: ToastClassName = (context) =>
  clsx(
    context?.defaultClassName,
    styles.Toast,
    context?.type && TYPE_CLASS[context.type]
  );

export const Toast = () => (
  <ToastContainer
    toastClassName={getToastClassName}
    icon={ToastIcon}
    closeButton={CloseButton}
    hideProgressBar
  />
);
